import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import cardNumberLocation from '@/assets/images/medicare-number-location-illustration.png';
import {
  HAPPY_HEARTS_SUPPORT_NUMBER,
  HAPPY_HEARTS_SUPPORT_TEL,
} from '@/components/happyHeartsSite';
import { CLOSE_MBI_HELP, OPEN_MBI_HELP } from '@/consts/events';
import { WCta } from '../WCta';
import { WregInfoModal, type WregInfoModalCloseMethod } from '../WregInfoModal';
import { WREG_WORKING_MS } from '../wregSteps';
import type { WregScreenEvent } from './wregScreenEvents';
import { INTAKE_MEDICARE_FIELDS, useIntakeErrors } from './wregIntakeErrors';
import { WregIntakeShell } from './WregIntakeShell';
import {
  EMPTY_INTAKE,
  MBI_MASK,
  formatMbi,
  intakeMedicareIsComplete,
  type WregIntakeForm,
} from './wregIntakeFields';

type Props = {
  notice?: string | null;
  initial?: WregIntakeForm;
  onSubmit: (form: WregIntakeForm) => void | Promise<void>;
  onSubmitted?: () => void;
  onEvent?: WregScreenEvent;
};

type Sheet = 'how' | 'why' | null;

const WregIntakeMedicare = ({ notice, initial, onSubmit, onSubmitted, onEvent }: Props) => {
  const { t } = useTranslation();
  const [f, setF] = useState<WregIntakeForm>(initial ?? EMPTY_INTAKE);
  const [sheet, setSheet] = useState<Sheet>(null);

  const valid = intakeMedicareIsComplete(f);
  const { errors, focus, blur, showError, errorId, validateBeforeSubmit } = useIntakeErrors({
    fields: INTAKE_MEDICARE_FIELDS,
    form: f,
    valid,
    step: 'intake-mbi',
    t,
    onEvent,
  });

  const open = (which: Exclude<Sheet, null>) => () => {
    onEvent?.(OPEN_MBI_HELP, { sheet: which });
    setSheet(which);
  };
  const close = (which: Exclude<Sheet, null>) => (method: WregInfoModalCloseMethod) => {
    onEvent?.(CLOSE_MBI_HELP, { sheet: which, method });
    setSheet(null);
  };

  return (
    <WregIntakeShell
      step="intake-mbi"
      title={t('wreg.intake.medicare.title')}
      sub={t('wreg.intake.medicare.sub')}
      notice={notice}
      onEvent={onEvent}
      overlay={
        <>
          <WregInfoModal
            open={sheet === 'how'}
            onClose={close('how')}
            titleId="wreg-mbi-how-title"
            title={t('wreg.intake.mbi.how.title')}
            titleSize="compact"
            sub={t('wreg.intake.mbi.how.sub')}
            image={{ src: cardNumberLocation, alt: t('wreg.mbi.alt'), width: 600, height: 400 }}
            closeLabel={t('wreg.mbi.close')}
          >
            <p className="rg-sub">
              <Trans
                i18nKey="wreg.intake.mbi.how.p2"
                components={{ 1: <span className="wreg-mbi-eg" /> }}
              />
            </p>
          </WregInfoModal>
          <WregInfoModal
            open={sheet === 'why'}
            onClose={close('why')}
            titleId="wreg-mbi-why-title"
            title={t('wreg.intake.mbi.why.title')}
            titleSize="compact"
            closeLabel={t('wreg.mbi.close')}
          >
            <p className="rg-sub">{t('wreg.intake.mbi.why.p1')}</p>
            <p className="rg-sub">
              <Trans
                i18nKey="wreg.intake.mbi.why.p2"
                components={{
                  1: (
                    <a
                      className="c-link"
                      href={t('general.links.cms-access-participants')}
                      target="_blank"
                      rel="noreferrer"
                    />
                  ),
                }}
              />
            </p>
            <p className="rg-sub">
              <Trans
                i18nKey="wreg.intake.mbi.why.p3"
                values={{ phone: HAPPY_HEARTS_SUPPORT_NUMBER }}
                components={{
                  1: <a className="wreg-mbi-tel" href={`tel:${HAPPY_HEARTS_SUPPORT_TEL}`} />,
                }}
              />
            </p>
          </WregInfoModal>
        </>
      }
      footer={
        <>
          <WCta
            beforeClick={validateBeforeSubmit}
            dimmed={!valid}
            working={t('wreg.intake.cta_loading')}
            minWorkingMs={WREG_WORKING_MS}
            onClick={() => onSubmit(f)}
            onDone={onSubmitted}
          >
            {t('wreg.intake.next')}
          </WCta>
        </>
      }
    >
      <div className="wreg-mbi-location">
        <img
          className="wreg-mbi-art"
          src={cardNumberLocation}
          width="600"
          height="400"
          alt={t('wreg.mbi.alt')}
        />
        <button type="button" className="c-link wreg-mbi-link wreg-mbi-why" onClick={open('why')}>
          {t('wreg.intake.mbi.why.link')}
        </button>
      </div>
      <div className="rg-field">
        <label className="rg-label" htmlFor="wreg-int-mbi">
          {t('wreg.intake.mbi.label')}
        </label>
        <div className="rg-input-wrap wreg-mbi-wrap">
          <input
            id="wreg-int-mbi"
            className="rg-input mbi"
            autoComplete="off"
            value={f.mbi}
            onChange={(e) => setF((p) => ({ ...p, mbi: formatMbi(e.target.value) }))}
            onFocus={focus('mbi')}
            onBlur={blur('mbi')}
            placeholder=""
            style={{ width: '18.5ch', maxWidth: '100%' }}
            aria-invalid={showError('mbi') || undefined}
            aria-describedby={showError('mbi') ? errorId('mbi') : undefined}
          />
          <span className="wreg-mbi-ghost" aria-hidden="true">
            <i>{f.mbi}</i>
            {MBI_MASK.slice(f.mbi.length)}
          </span>
        </div>
        <span className="rg-hint">{t('wreg.intake.mbi.hint')}</span>
        {showError('mbi') && (
          <span className="rg-error-hint" id={errorId('mbi')} role="alert">
            {errors.mbi}
          </span>
        )}
        <button type="button" className="c-link wreg-mbi-link wreg-mbi-how" onClick={open('how')}>
          {t('wreg.intake.mbi.how.link')}
        </button>
      </div>
    </WregIntakeShell>
  );
};

export { WregIntakeMedicare };
